import { useEffect, useState } from "react";
import { Loader2, Languages, Save, Trash2 } from "lucide-react";
import { getProfile, updateProfile, translateFields } from "@/lib/adminContentApi";
import { TextField, TextAreaField, RepeatableStrings, deText } from "./fields";
import { formatApiError } from "@/lib/api";

function toFormState(p) {
  return {
    headline: deText(p.headline),
    location: deText(p.location),
    birth_place: deText(p.birth_place),
    nationality: deText(p.nationality),
    birth_date: p.birth_date || "",
    intro: deText(p.intro),
    availability: deText(p.availability),
    flexibility: deText(p.flexibility),
    phone: p.phone || "",
    email: p.email || "",
    email_personal: p.email_personal || "",
    linkedin: p.social?.linkedin || "",
    github: p.social?.github || "",
    xing: p.social?.xing || "",
    cv_pdf_de: p.cv_pdf_de || "",
    cv_pdf_en: p.cv_pdf_en || "",
    mottos: (p.mottos || []).map(deText),
    hobbies: (p.hobbies || []).map(deText),
    core_skills: (p.core_skills || []).map(deText),
    languages: (p.languages || []).map((l) => ({ name: deText(l.name), level: deText(l.level) })),
  };
}

export default function ProfileEditor() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getProfile()
      .then((p) => setForm(toFormState(p)))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const onSave = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const texts = {
        headline: form.headline,
        location: form.location,
        birth_place: form.birth_place,
        nationality: form.nationality,
        intro: form.intro,
        availability: form.availability,
        flexibility: form.flexibility,
      };
      form.mottos.forEach((v, i) => {
        if (v.trim()) texts[`motto_${i}`] = v;
      });
      form.hobbies.forEach((v, i) => {
        if (v.trim()) texts[`hobby_${i}`] = v;
      });
      form.core_skills.forEach((v, i) => {
        if (v.trim()) texts[`core_skill_${i}`] = v;
      });
      form.languages.forEach((l, i) => {
        if (l.name.trim()) texts[`lang_name_${i}`] = l.name;
        if (l.level.trim()) texts[`lang_level_${i}`] = l.level;
      });
      const { en = {}, fr = {} } = await translateFields(texts);
      const ml = (key, fallback) => ({ de: fallback, en: en[key] || fallback, fr: fr[key] || fallback });
      const payload = {
        headline: ml("headline", form.headline),
        location: ml("location", form.location),
        birth_place: ml("birth_place", form.birth_place),
        nationality: ml("nationality", form.nationality),
        birth_date: form.birth_date,
        intro: ml("intro", form.intro),
        availability: ml("availability", form.availability),
        flexibility: ml("flexibility", form.flexibility),
        phone: form.phone,
        email: form.email,
        email_personal: form.email_personal,
        social: { linkedin: form.linkedin, github: form.github, xing: form.xing },
        cv_pdf_de: form.cv_pdf_de,
        cv_pdf_en: form.cv_pdf_en,
        mottos: form.mottos.map((v, i) => (v.trim() ? ml(`motto_${i}`, v) : null)).filter(Boolean),
        hobbies: form.hobbies.map((v, i) => (v.trim() ? ml(`hobby_${i}`, v) : null)).filter(Boolean),
        core_skills: form.core_skills.map((v, i) => (v.trim() ? ml(`core_skill_${i}`, v) : null)).filter(Boolean),
        languages: form.languages
          .map((l, i) => (l.name.trim() ? { name: ml(`lang_name_${i}`, l.name), level: ml(`lang_level_${i}`, l.level) } : null))
          .filter(Boolean),
      };
      await updateProfile(payload);
      setSaved(true);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-[var(--text-2)] text-sm py-8 text-center">Laden…</div>;
  if (!form) return null;

  return (
    <div data-testid="profile-admin">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h2 className="font-display text-2xl text-[var(--text-0)]">Profiltexte</h2>
          <p className="text-sm text-[var(--text-2)] mt-1 max-w-xl">Persönliche Texte, Motto, Hobbys, Sprachen &amp; Kontaktdaten.</p>
        </div>
        <button onClick={onSave} disabled={saving} data-testid="profile-save-btn" className="btn-primary text-xs disabled:opacity-50">
          {saving ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Speichert &amp; übersetzt…
            </>
          ) : (
            <>
              <Save size={14} /> Speichern
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="border border-red-500/40 text-red-500 p-3 mb-4 text-sm" data-testid="profile-error">
          {error}
        </div>
      )}
      {saved && !error && (
        <div className="border border-emerald-500/40 text-emerald-600 p-3 mb-4 text-sm" data-testid="profile-saved">
          Gespeichert &amp; übersetzt.
        </div>
      )}

      <div className="flex items-start gap-2 text-xs text-[var(--gold)] bg-[var(--gold)]/5 border border-[var(--gold)]/20 p-3 mb-6">
        <Languages size={14} className="shrink-0 mt-0.5" />
        <span>Nur Deutsch ausfüllen – Englisch &amp; Französisch werden beim Speichern automatisch übersetzt.</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-5">
          <TextField label="Headline" testid="profile-headline" value={form.headline} onChange={(v) => setForm({ ...form, headline: v })} />
          <TextAreaField label="Intro-Text" testid="profile-intro" value={form.intro} onChange={(v) => setForm({ ...form, intro: v })} rows={5} />
          <TextField label="Standort" testid="profile-location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} />
          <TextField label="Verfügbarkeit" testid="profile-availability" value={form.availability} onChange={(v) => setForm({ ...form, availability: v })} />
          <TextField label="Flexibilität" testid="profile-flexibility" value={form.flexibility} onChange={(v) => setForm({ ...form, flexibility: v })} />
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Geburtsort" testid="profile-birth-place" value={form.birth_place} onChange={(v) => setForm({ ...form, birth_place: v })} />
            <TextField label="Nationalität" testid="profile-nationality" value={form.nationality} onChange={(v) => setForm({ ...form, nationality: v })} />
          </div>
          <TextField
            label="Geburtsdatum"
            testid="profile-birth-date"
            value={form.birth_date}
            onChange={(v) => setForm({ ...form, birth_date: v })}
            placeholder="TT.MM.JJJJ"
          />
        </div>

        <div className="space-y-5">
          <RepeatableStrings label="Mottos" testid="profile-motto" items={form.mottos} onChange={(v) => setForm({ ...form, mottos: v })} />
          <RepeatableStrings label="Kernkompetenzen" testid="profile-core-skill" items={form.core_skills} onChange={(v) => setForm({ ...form, core_skills: v })} />
          <RepeatableStrings label="Hobbys" testid="profile-hobby" items={form.hobbies} onChange={(v) => setForm({ ...form, hobbies: v })} />
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)]">Sprachen</span>
              <button
                type="button"
                onClick={() => setForm({ ...form, languages: [...form.languages, { name: "", level: "" }] })}
                data-testid="profile-lang-add"
                className="text-xs text-[var(--gold)] hover:underline"
              >
                + Hinzufügen
              </button>
            </div>
            <div className="space-y-2">
              {form.languages.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_32px] gap-2 items-center">
                  <input
                    value={l.name}
                    onChange={(e) => {
                      const next = [...form.languages];
                      next[i] = { ...next[i], name: e.target.value };
                      setForm({ ...form, languages: next });
                    }}
                    placeholder="Sprache"
                    data-testid={`profile-lang-name-${i}`}
                    className="bg-transparent border-b hairline focus:border-[var(--gold)] outline-none py-1.5 text-[var(--text-0)] text-sm"
                  />
                  <input
                    value={l.level}
                    onChange={(e) => {
                      const next = [...form.languages];
                      next[i] = { ...next[i], level: e.target.value };
                      setForm({ ...form, languages: next });
                    }}
                    placeholder="Niveau"
                    data-testid={`profile-lang-level-${i}`}
                    className="bg-transparent border-b hairline focus:border-[var(--gold)] outline-none py-1.5 text-[var(--text-0)] text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, languages: form.languages.filter((_, idx) => idx !== i) })}
                    data-testid={`profile-lang-remove-${i}`}
                    className="text-[var(--text-3)] hover:text-red-500"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 pt-8 border-t hairline grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)] mb-1">Kontakt (nicht übersetzt)</div>
          <TextField label="Telefon" testid="profile-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
          <TextField label="E-Mail (geschäftlich)" testid="profile-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
          <TextField label="E-Mail (privat)" testid="profile-email-personal" value={form.email_personal} onChange={(v) => setForm({ ...form, email_personal: v })} />
        </div>
        <div className="space-y-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--text-2)] mb-1">Links &amp; Dateien</div>
          <TextField label="LinkedIn" testid="profile-linkedin" value={form.linkedin} onChange={(v) => setForm({ ...form, linkedin: v })} placeholder="https://linkedin.com/in/..." />
          <TextField label="GitHub" testid="profile-github" value={form.github} onChange={(v) => setForm({ ...form, github: v })} placeholder="https://github.com/..." />
          <TextField label="Xing" testid="profile-xing" value={form.xing} onChange={(v) => setForm({ ...form, xing: v })} placeholder="https://xing.com/..." />
          <TextField label="CV PDF (Deutsch)" testid="profile-cv-de" value={form.cv_pdf_de} onChange={(v) => setForm({ ...form, cv_pdf_de: v })} />
          <TextField label="CV PDF (Englisch)" testid="profile-cv-en" value={form.cv_pdf_en} onChange={(v) => setForm({ ...form, cv_pdf_en: v })} />
        </div>
      </div>
    </div>
  );
}
